// Column-segment parser for the CREATE TABLE extractor. Splits a single
// comma-separated segment of the column list into either a `ColumnDef`
// (simple column) or an opaque `SqlRange` (table constraint, computed column,
// inline foreign key — all v1-out-of-scope and surfaced as opaque so the
// round-trip preserves them byte-for-byte).
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: DDL Structure Extractor —
//      CREATE TABLE. Acceptance criteria call out: computed column → opaque,
//      foreign key inline → opaque (kept verbatim in `opaqueRanges`).

import type { ParseOptions, SqlRange } from '../../../sql-grammar/model';
import { rangeFromOffsets, type LineIndex } from '../../../sql-grammar/document/lineIndex';
import { readIdentifier } from '../../scope/identifiers';
import type { ColumnDef } from '../../types';

import { findKeywordAtTopLevel, skipWsCommentsLocal } from './shared';

/** Local segment trim — drops leading whitespace + comments (via the
 *  identifier-safe `skipWsCommentsLocal`) and trailing whitespace. We do NOT
 *  use the structure-extractor's `trimRange`, because its leading-edge
 *  fallback to `trySkipNonCode` would consume bracket / backtick / double-
 *  quoted identifiers — exactly the column / table names the DDL extractor
 *  needs to keep. */
function trimSegment(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): { readonly startOffset: number; readonly endOffset: number } {
  const s = skipWsCommentsLocal(source, start, end, options);
  let e = end;
  while (e > s) {
    const c = source[e - 1];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { e--; continue; }
    break;
  }
  return { startOffset: s, endOffset: e };
}

/** Keywords that, when they appear as the FIRST identifier of a segment,
 *  signal a table-level constraint rather than a column definition. Per-engine
 *  variants (FULLTEXT/SPATIAL — MySQL, EXCLUDE — Postgres, INHERITS — PG, LIKE
 *  — PG/sqlite copy-form) are all included so the segment reader doesn't need
 *  per-engine dispatch for the simple "is this a constraint?" decision. */
const TABLE_CONSTRAINT_LEAD_KEYWORDS: ReadonlySet<string> = new Set([
  'CONSTRAINT', 'PRIMARY', 'UNIQUE', 'FOREIGN', 'CHECK',
  'KEY', 'INDEX', 'FULLTEXT', 'SPATIAL', 'EXCLUDE',
  'INHERITS', 'LIKE', 'PERIOD',
]);

/** Keywords that, when they appear at top-level inside a column segment AFTER
 *  the column name, signal that the column is out-of-scope for the v1 simple
 *  ColumnDef shape (inline FK). The whole segment is then surfaced as an opaque
 *  range.
 *
 *  NOTE: `GENERATED` is deliberately NOT here. It is overloaded — a *computed*
 *  column (`GENERATED ALWAYS AS (expr) …`) is still surfaced as opaque, but via
 *  `hasComputedColumnMarker` (which detects the `AS (` paren form). An *identity*
 *  column (`GENERATED ALWAYS|BY DEFAULT AS IDENTITY …`) has no `AS (`, so it
 *  parses as a normal `ColumnDef` — consistent with MSSQL `IDENTITY` / MySQL
 *  `AUTO_INCREMENT` (both type-terminators below). `GENERATED` is itself a type
 *  terminator so the declared type stops before the identity clause. */
const OPAQUE_COLUMN_MARKERS: ReadonlySet<string> = new Set([
  'REFERENCES',
]);

/** Keywords that terminate the type-text run in a simple column. Anything
 *  before the first occurrence of one of these (at paren-depth 0) is the
 *  declared type; after it lies column-level constraint text. The set is the
 *  union across engines — lookups are O(1) so over-inclusion is harmless. */
const TYPE_TERMINATORS: ReadonlySet<string> = new Set([
  'NULL', 'NOT', 'DEFAULT', 'IDENTITY', 'AUTO_INCREMENT', 'AUTOINCREMENT',
  'GENERATED', 'PRIMARY', 'UNIQUE', 'COLLATE', 'COMMENT', 'CONSTRAINT',
  'INDEX', 'KEY', 'CHECK', 'VISIBLE', 'INVISIBLE',
  'STORED', 'VIRTUAL', 'PERSISTED', 'ROWGUIDCOL', 'SPARSE',
  'ENABLE', 'DISABLE', 'ENCRYPTED', 'MASKED', 'COMPRESSED',
  'ON', 'CHARACTER',
]);

/** Detect "AS (" as a top-level computed-column expression marker. We treat
 *  it as opaque because the expression body is out-of-scope for v1 parsing. */
function hasComputedColumnMarker(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): boolean {
  const hit = findKeywordAtTopLevel(source, options, start, end, new Set(['AS']));
  if (!hit) return false;
  const next = skipWsCommentsLocal(source, hit.end, end, options);
  return source[next] === '(';
}

/** Read the verbatim type text starting at `start` (after the column name).
 *  Returns the substring and the offset just past the type. */
function readTypeText(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): { readonly text: string; readonly endOffset: number } {
  const hit = findKeywordAtTopLevel(source, options, start, end, TYPE_TERMINATORS);
  const stop = hit ? hit.start : end;
  const trimmed = trimSegment(source, options, start, stop);
  return {
    text: source.slice(trimmed.startOffset, trimmed.endOffset),
    endOffset: stop,
  };
}

/** Inspect the post-type tail of a column segment for the `NULL` / `NOT NULL`
 *  marker. Returns:
 *  - true  → declared NULL,
 *  - false → declared NOT NULL,
 *  - undefined → not declared (caller leaves the field undefined). */
function detectNullable(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): boolean | undefined {
  const notHit = findKeywordAtTopLevel(source, options, start, end, new Set(['NOT']));
  if (notHit) {
    const next = skipWsCommentsLocal(source, notHit.end, end, options);
    const after = readIdentifier(source, next, end, options);
    if (after && !after.quoted && after.value.toUpperCase() === 'NULL') return false;
  }
  const nullHit = findKeywordAtTopLevel(source, options, start, end, new Set(['NULL']));
  if (nullHit) return true;
  return undefined;
}

/** Parse a single comma-separated segment of the CREATE TABLE column list.
 *  Pushes either a `ColumnDef` (simple column) or an opaque range onto the
 *  caller's accumulators. */
export function parseColumnOrConstraintSegment(
  source: string,
  options: ParseOptions,
  lineIdx: LineIndex,
  segStart: number,
  segEnd: number,
  columns: ColumnDef[],
  opaqueRanges: SqlRange[],
): void {
  const trimmed = trimSegment(source, options, segStart, segEnd);
  if (trimmed.startOffset >= trimmed.endOffset) return;
  const start = trimmed.startOffset;
  const end = trimmed.endOffset;

  const first = readIdentifier(source, start, end, options);
  if (!first) {
    opaqueRanges.push(rangeFromOffsets(lineIdx, start, end));
    return;
  }

  if (!first.quoted && TABLE_CONSTRAINT_LEAD_KEYWORDS.has(first.value.toUpperCase())) {
    opaqueRanges.push(rangeFromOffsets(lineIdx, start, end));
    return;
  }

  // Simple column: name + type + optional NULL/NOT NULL/DEFAULT.
  const afterName = first.end;
  if (findKeywordAtTopLevel(source, options, afterName, end, OPAQUE_COLUMN_MARKERS)
      || hasComputedColumnMarker(source, options, afterName, end)) {
    opaqueRanges.push(rangeFromOffsets(lineIdx, start, end));
    return;
  }

  const typeStart = skipWsCommentsLocal(source, afterName, end, options);
  const { text: typeRef, endOffset: typeEnd } = readTypeText(source, options, typeStart, end);
  if (!typeRef) {
    opaqueRanges.push(rangeFromOffsets(lineIdx, start, end));
    return;
  }

  const nullable = detectNullable(source, options, typeEnd, end);
  const column: ColumnDef = {
    name: first.value,
    typeRef,
    range: rangeFromOffsets(lineIdx, start, end),
    ...(nullable !== undefined ? { nullable } : {}),
    // The name token's own delimiters. `first.value` has them stripped, so
    // without this a `CREATE TABLE "t" ("Id" integer)` is indistinguishable
    // from `CREATE TABLE t (Id integer)` — and every later edit that NAMES
    // that column folds it to `id` / `ID` on the two folding engines, naming
    // a column the delimited CREATE never created. The quoting was already in
    // hand here and only had to stop being dropped; the ALTER TABLE
    // classifier records the same fact for its own `ADD COLUMN` clauses.
    nameQuoting: first.quoting,
  };
  columns.push(column);
}
